import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ModuleNav } from "./ModuleNav";
import { Download, Search, Send, CheckCircle2, AlertTriangle } from "lucide-react";

const STATUS_STYLE = {
  Delivered: "bg-[#134e4a] text-white",
  Sent: "bg-teal-100 text-teal-900",
  "Pending delivery": "bg-amber-100 text-amber-900",
  Failed: "bg-red-800 text-white",
};

const Stat = ({ label, value, sub, tone = "" }) => (
  <div className="border border-slate-200 p-3" data-testid={`dist-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`num mt-0.5 text-2xl font-bold ${tone || "text-slate-900"}`}>{value}</div>
    {sub && <div className="text-xs text-slate-500">{sub}</div>}
  </div>
);

export default function ReportDistribution() {
  const [meta, setMeta] = useState(null);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ q: "", flag: "" });
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const p = { ...filters };
      Object.keys(p).forEach((k) => !p[k] && delete p[k]);
      const [list, sum] = await Promise.all([
        api.get("/report-distributions", { params: p }),
        api.get("/report-distributions/summary", { params: { days: 180 } }),
      ]);
      setRows(list.data);
      setSummary(sum.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [filters]);

  useEffect(() => { api.get("/report-distributions/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const canEdit = !!meta?.can_edit;

  const save = async () => {
    setBusy("detail");
    try {
      await api.put(`/report-distributions/${detail.id}`, {
        recipient: detail.recipient || "", recipient_role: detail.recipient_role || "Ordering physician",
        destination: detail.destination || "", status: detail.status,
        delivered_at: detail.delivered_at || undefined, confirmed_by: detail.confirmed_by || "",
        read_back_confirmed: !!detail.read_back_confirmed, notes: detail.notes || "",
      });
      toast.success("Distribution record saved");
      setDetail(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const markDelivered = async (row) => {
    setBusy(row.id);
    try {
      await api.post(`/report-distributions/${row.id}/delivered`);
      toast.success("Delivery confirmed");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/report-distributions/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "PathQity_ReportDistribution.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("CSV downloaded");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="report-distribution-page">
      <ModuleNav
        title="Report Distribution"
        blurb="Proof that every signed and amended report reached the ordering clinician — how it was sent, who confirmed receipt and how long it took."
        actions={
          <Button variant="outline" onClick={exportCsv} data-testid="dist-export-button" className="h-11 border-slate-300">
            <Download size={15} className="mr-2" /> Export CSV
          </Button>
        }
      />

      {summary && (
        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="dist-metrics">
          <Stat label="Reports (180 days)" value={summary.total}
                sub={`${summary.amended} amended · ${summary.final} original final`} />
          <Stat label="Delivery confirmed"
                value={summary.delivered_percent === null ? "—" : `${summary.delivered_percent}%`}
                sub={`${summary.pending} awaiting confirmation`}
                tone={summary.pending ? "text-amber-700" : "text-[#134e4a]"} />
          <Stat label="Amendments outstanding" value={summary.amended_pending}
                sub={`${summary.overdue} past the ${summary.deadline_hours} h deadline`}
                tone={summary.overdue ? "text-red-700" : "text-[#134e4a]"} />
          <Stat label="Average time to deliver"
                value={summary.average_hours === null ? "—" : `${summary.average_hours} h`}
                sub={summary.longest_hours === null ? "" : `longest ${summary.longest_hours} h · ${summary.read_back_documented} read-backs documented`} />
        </section>
      )}

      <div className="flex flex-wrap items-center gap-2" data-testid="dist-filters">
        <div className="relative w-full sm:w-64">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                 placeholder="Accession, patient, clinician" data-testid="dist-search" className="h-10 pl-9" />
        </div>
        {[["", "All"], ["amended", "Amended"], ["amended_pending", "Amendments outstanding"],
          ["pending", "Awaiting confirmation"], ["overdue", "Past deadline"]].map(([k, label]) => (
          <button key={k || "all"} onClick={() => setFilters((f) => ({ ...f, flag: k }))}
                  data-testid={`dist-filter-${k || "all"}`}
                  className={`h-10 border px-3 text-sm font-medium ${filters.flag === k
                    ? "border-[#134e4a] bg-[#134e4a] text-white" : "border-slate-300 bg-white text-slate-700 hover:border-[#134e4a]"}`}>
            {label}
          </button>
        ))}
      </div>

      <section className="border border-slate-200" data-testid="dist-log">
        {rows.length === 0 ? (
          <p className="px-3 py-10 text-center text-sm text-slate-500" data-testid="dist-empty">
            Nothing to show for this filter. Records appear here the moment a report is signed out or amended.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1060px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr className="border-b border-slate-200">
                  <th className="px-3 py-2">Case</th><th className="px-3 py-2">Report</th>
                  <th className="px-3 py-2">Signed</th><th className="px-3 py-2">Recipient</th>
                  <th className="px-3 py-2">Route</th><th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Delivered</th><th className="px-3 py-2 text-right">Hours</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={`border-b border-slate-100 row-hover hover:bg-slate-50 ${
                        r.amended ? "bg-amber-50/40" : ""}`} data-testid={`dist-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <Link to={`/cases/${r.case_id}/report`} className="font-mono font-semibold text-[#134e4a] hover:underline">
                        {r.accession_number}
                      </Link>
                      <div className="text-xs text-slate-500">{r.patient_name}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 text-[11px] font-bold uppercase ${
                        r.amended ? "bg-amber-200 text-amber-950" : "bg-slate-100 text-slate-700"}`}>
                        {r.report_kind} v{r.report_version}
                      </span>
                      {r.amendment_reason && (
                        <div className="max-w-[16rem] truncate text-[11px] text-slate-500">{r.amendment_reason}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {fmtDateTime(r.signed_at)}
                      <div className="text-[11px] text-slate-400">{r.signed_by}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-800">
                      {r.recipient || <span className="text-slate-400">not recorded</span>}
                      <div className="text-[11px] text-slate-500">{r.recipient_role}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-700">{r.destination || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[r.status] || "bg-slate-200"}`}>
                        {r.status}
                      </span>
                      {r.overdue && (
                        <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-bold text-red-800">
                          <AlertTriangle size={11} /> past {r.deadline_hours} h
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {r.delivered_at ? fmtDateTime(r.delivered_at) : <span className="text-amber-800">not confirmed</span>}
                      {r.confirmed_by && <div className="text-[11px] text-slate-400">by {r.confirmed_by}</div>}
                    </td>
                    <td className={`num px-3 py-2 text-right font-bold ${
                      r.late_delivery ? "text-red-700" : r.hours_to_deliver !== null ? "text-[#134e4a]" : "text-amber-800"}`}>
                      {r.hours_to_deliver !== null ? r.hours_to_deliver
                        : r.hours_since_signed !== null ? `${r.hours_since_signed}…` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="flex flex-wrap justify-end gap-1.5">
                        {canEdit && !r.delivered && r.destination && (
                          <Button size="sm" disabled={busy === r.id} onClick={() => markDelivered(r)}
                                  data-testid={`dist-confirm-${r.id}`} className="h-9 bg-[#134e4a] hover:bg-[#0f3f3c]">
                            <CheckCircle2 size={13} className="mr-1" /> Confirm
                          </Button>
                        )}
                        {canEdit && (
                          <Button size="sm" variant="outline" onClick={() => setDetail({ ...r })}
                                  data-testid={`dist-edit-${r.id}`} className="h-9 border-slate-300">
                            <Send size={13} className="mr-1" /> Record
                          </Button>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="dist-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              Record distribution — <span className="font-mono">{detail?.accession_number}</span>{" "}
              {detail?.report_kind} v{detail?.report_version}
            </DialogTitle>
            <DialogDescription>
              Record how the report was sent and who confirmed receipt.
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Recipient clinician</Label>
                <Input value={detail.recipient || ""} data-testid="dist-recipient"
                       onChange={(e) => setDetail((d) => ({ ...d, recipient: e.target.value }))}
                       className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Recipient role</Label>
                <Input value={detail.recipient_role || ""} data-testid="dist-recipient-role"
                       onChange={(e) => setDetail((d) => ({ ...d, recipient_role: e.target.value }))}
                       className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">How it was sent</Label>
                <select value={detail.destination || ""} data-testid="dist-destination"
                        onChange={(e) => setDetail((d) => ({ ...d, destination: e.target.value }))}
                        className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                  <option value="">Select a route…</option>
                  {(meta?.destinations || []).map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Status</Label>
                <select value={detail.status} data-testid="dist-status"
                        onChange={(e) => setDetail((d) => ({ ...d, status: e.target.value }))}
                        className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.statuses || []).map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Delivered / sent at</Label>
                <Input type="datetime-local" data-testid="dist-delivered-at"
                       value={(detail.delivered_at || "").slice(0, 16)}
                       onChange={(e) => setDetail((d) => ({ ...d, delivered_at: e.target.value }))}
                       className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Receipt confirmed by</Label>
                <Input value={detail.confirmed_by || ""} data-testid="dist-confirmed-by"
                       placeholder="Person at the clinic or interface ack"
                       onChange={(e) => setDetail((d) => ({ ...d, confirmed_by: e.target.value }))}
                       className="mt-1 h-11 border-slate-300" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
                <input type="checkbox" checked={!!detail.read_back_confirmed} data-testid="dist-readback"
                       onChange={(e) => setDetail((d) => ({ ...d, read_back_confirmed: e.target.checked }))}
                       className="h-4 w-4 accent-[#134e4a]" />
                Read-back confirmed by the receiver (phoned amendments)
              </label>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Notes</Label>
                <Textarea value={detail.notes || ""} data-testid="dist-notes"
                          onChange={(e) => setDetail((d) => ({ ...d, notes: e.target.value }))}
                          className="mt-1 min-h-[70px] border-slate-300" />
              </div>
              {detail.amended && (
                <p className="sm:col-span-2 border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-slate-700">
                  This is an amended report. CAP expects documented delivery to the ordering clinician within{" "}
                  {detail.deadline_hours} hours of signing.
                </p>
              )}
              <Button onClick={save} disabled={busy === "detail"} data-testid="dist-detail-save"
                      className="h-11 sm:col-span-2 bg-[#134e4a] hover:bg-[#0f3f3c]">
                {busy === "detail" ? "Saving…" : "Save distribution record"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
